import { useRef, useEffect } from 'react';
import { useAnalyticsClient } from '../analytics/analytics-web-client';

const recaptchaIframeTitle = 'recaptcha challenge';

export const useRecaptchaChallengeObserver = (pageId: string) => {
  const analyticsClient = useAnalyticsClient();
  const bodyObserver = useRef<MutationObserver>();
  const attributeObserver = useRef<MutationObserver>();

  useEffect(() => {
    /*
      To determine if user has been presented with a Google reCAPTCHA challenge, we need to listen to DOM events and check if an <iframe> was injected.
      Said <iframe> will have 'src' attribute containing 'google.com/recaptcha/api2/bframe'. Unfortunately, that is not enough
      because Google will inject the <iframe> even if they're not gonna show the challenge. So we attach another observer,
      looking for a change on the 'style' attribute of the outmost containing <div>. If style.target.visibility is 'visible',
      then we send analytics event.
    */
    const outerContainerAttributeObserver = () => {
      return new MutationObserver(records => {
        const visibleEvent = Array.from(records).find(
          mutation =>
            mutation.attributeName === 'style' &&
            (mutation.target as HTMLIFrameElement).style.visibility === 'visible'
        );

        if (visibleEvent) {
          analyticsClient.trackingEvent({
            page: pageId,
            action: 'challenge',
            subject: 'googleRecaptcha',
          });

          attributeObserver.current?.disconnect();
        }
      });
    };

    if (window.MutationObserver) {
      bodyObserver.current = new MutationObserver(records => {
        records.forEach(mutation => {
          mutation.addedNodes.forEach(node => {
            if (!(node instanceof Element)) {
              return;
            }

            const recaptchaIframe = node.querySelector(`iframe[title*='${recaptchaIframeTitle}']`);

            if (recaptchaIframe) {
              bodyObserver.current?.disconnect();
              const recaptchaOuterDiv = recaptchaIframe.parentNode?.parentNode;

              if (recaptchaOuterDiv) {
                attributeObserver.current = outerContainerAttributeObserver();
                attributeObserver.current?.observe(recaptchaOuterDiv, {
                  attributes: true,
                });
              }
            }
          });
        });
      });

      bodyObserver.current?.observe(window.document.body, { childList: true });
    }

    return () => {
      bodyObserver.current?.disconnect();
      attributeObserver.current?.disconnect();
    };
  }, [analyticsClient, pageId]);
};
